import { create } from 'zustand';
import type { Message, WorkPlan, Conversation } from '@/types';

export type BuildStatus = 'idle' | 'building' | 'success' | 'failed';
export type DeployStatus = 'idle' | 'deploying' | 'success' | 'failed';

export interface FileOperation {
  operation: 'create' | 'edit' | 'delete';
  path: string;
  timestamp: Date;
}

export interface CommandOutput {
  command: string;
  output: string;
  exitCode?: number;
  timestamp: Date;
}

// Agentic AI types
export interface ThinkingBlock {
  id: string;
  content: string;
  isComplete: boolean;
}

export interface ToolOperation {
  id: string;
  toolName: string;
  description: string;
  status: 'running' | 'success' | 'error';
  summary?: string;
  errorMessage?: string;
}

export interface QuestionOption {
  id: string;
  label: string;
  description?: string;
}

export interface PendingQuestion {
  questionId: string;
  question: string;
  options?: QuestionOption[];
  allowMultiple: boolean;
  allowCustom: boolean;
  context?: string;
}

export interface UserPreferences {
  showThinking: boolean;
  thinkingDefaultExpanded: boolean;
}

interface ChatState {
  conversations: Conversation[];
  currentConversation: Conversation | null;
  messages: Message[];
  currentStreamingMessage: string;
  workPlan: WorkPlan | null;
  isStreaming: boolean;
  error: string | null;

  // State for vibe coding experience
  thinkingStatus: string | null;
  terminalOutput: CommandOutput[];
  fileOperations: FileOperation[];
  buildStatus: BuildStatus;
  deployStatus: DeployStatus;
  lastFileChange: Date | null;

  // Agentic AI state
  thinkingBlocks: ThinkingBlock[];
  toolOperations: ToolOperation[];
  pendingQuestion: PendingQuestion | null;
  userPreferences: UserPreferences;

  setConversations: (conversations: Conversation[]) => void;
  setCurrentConversation: (conversation: Conversation | null) => void;
  setMessages: (messages: Message[]) => void;
  addMessage: (message: Message) => void;
  appendToStreamingMessage: (delta: string) => void;
  finalizeStreamingMessage: () => void;
  setWorkPlan: (workPlan: WorkPlan | null) => void;
  updateWorkPlan: (updates: Partial<WorkPlan>) => void;
  updateWorkPlanPhase: (phaseId: string, updates: Record<string, unknown>) => void;
  updateWorkPlanTask: (phaseId: string, taskId: string, updates: Record<string, unknown>) => void;
  setStreaming: (streaming: boolean) => void;
  setError: (error: string | null) => void;

  // Actions for vibe coding experience
  setThinkingStatus: (status: string | null) => void;
  addTerminalOutput: (output: CommandOutput) => void;
  addFileOperation: (operation: FileOperation) => void;
  setBuildStatus: (status: BuildStatus) => void;
  setDeployStatus: (status: DeployStatus) => void;

  // Agentic AI actions
  updateThinkingBlock: (blockId: string, content: string, isComplete: boolean) => void;
  addToolOperation: (operation: ToolOperation) => void;
  updateToolOperation: (toolId: string, updates: Partial<ToolOperation>) => void;
  setPendingQuestion: (question: PendingQuestion | null) => void;
  setUserPreferences: (prefs: Partial<UserPreferences>) => void;

  reset: () => void;
}

export const useChatStore = create<ChatState>((set, get) => ({
  conversations: [],
  currentConversation: null,
  messages: [],
  currentStreamingMessage: '',
  workPlan: null,
  isStreaming: false,
  error: null,

  // State for vibe coding experience
  thinkingStatus: null,
  terminalOutput: [],
  fileOperations: [],
  buildStatus: 'idle',
  deployStatus: 'idle',
  lastFileChange: null,

  // Agentic AI state
  thinkingBlocks: [],
  toolOperations: [],
  pendingQuestion: null,
  userPreferences: {
    showThinking: true,
    thinkingDefaultExpanded: false,
  },

  setConversations: (conversations) =>
    set({ conversations }),

  setCurrentConversation: (conversation) =>
    set({ currentConversation: conversation }),

  setMessages: (messages) =>
    set({ messages }),

  addMessage: (message) =>
    set((state) => ({
      messages: [...state.messages, message],
    })),

  appendToStreamingMessage: (delta) =>
    set((state) => ({
      currentStreamingMessage: state.currentStreamingMessage + delta,
    })),

  finalizeStreamingMessage: () => {
    const { currentStreamingMessage, messages } = get();
    if (currentStreamingMessage) {
      set({
        messages: [
          ...messages,
          {
            role: 'Assistant',
            content: currentStreamingMessage,
            createdAt: new Date(),
          },
        ],
        currentStreamingMessage: '',
      });
    }
  },

  setWorkPlan: (workPlan) =>
    set({ workPlan }),

  updateWorkPlan: (updates) =>
    set((state) => ({
      workPlan: state.workPlan ? { ...state.workPlan, ...updates } : null,
    })),

  updateWorkPlanPhase: (phaseId, updates) =>
    set((state) => {
      if (!state.workPlan) return state;
      return {
        workPlan: {
          ...state.workPlan,
          phases: state.workPlan.phases.map((phase) =>
            phase.id === phaseId ? { ...phase, ...updates } : phase
          ),
        },
      };
    }),

  updateWorkPlanTask: (phaseId, taskId, updates) =>
    set((state) => {
      if (!state.workPlan) return state;
      return {
        workPlan: {
          ...state.workPlan,
          phases: state.workPlan.phases.map((phase) =>
            phase.id === phaseId
              ? {
                  ...phase,
                  tasks: phase.tasks.map((task) =>
                    task.id === taskId ? { ...task, ...updates } : task
                  ),
                }
              : phase
          ),
        },
      };
    }),

  setStreaming: (streaming) =>
    set({ isStreaming: streaming }),

  setError: (error) =>
    set({ error }),

  // New actions for vibe coding experience
  setThinkingStatus: (status) =>
    set({ thinkingStatus: status }),

  addTerminalOutput: (output) =>
    set((state) => ({
      terminalOutput: [...state.terminalOutput, output],
    })),

  addFileOperation: (operation) =>
    set((state) => ({
      fileOperations: [...state.fileOperations, operation],
      lastFileChange: new Date(),
    })),

  setBuildStatus: (status) =>
    set({ buildStatus: status }),

  setDeployStatus: (status) =>
    set({ deployStatus: status }),

  // Agentic AI actions
  updateThinkingBlock: (blockId, content, isComplete) =>
    set((state) => {
      const existingIndex = state.thinkingBlocks.findIndex(b => b.id === blockId);
      if (existingIndex >= 0) {
        const updated = [...state.thinkingBlocks];
        updated[existingIndex] = {
          ...updated[existingIndex],
          content: updated[existingIndex].content + content,
          isComplete,
        };
        return { thinkingBlocks: updated };
      } else {
        return {
          thinkingBlocks: [...state.thinkingBlocks, { id: blockId, content, isComplete }],
        };
      }
    }),

  addToolOperation: (operation) =>
    set((state) => ({
      toolOperations: [...state.toolOperations, operation],
    })),

  updateToolOperation: (toolId, updates) =>
    set((state) => ({
      toolOperations: state.toolOperations.map((op) =>
        op.id === toolId ? { ...op, ...updates } : op
      ),
    })),

  setPendingQuestion: (question) =>
    set({ pendingQuestion: question }),

  setUserPreferences: (prefs) =>
    set((state) => ({
      userPreferences: { ...state.userPreferences, ...prefs },
    })),

  reset: () =>
    set({
      messages: [],
      currentStreamingMessage: '',
      workPlan: null,
      isStreaming: false,
      error: null,
      thinkingStatus: null,
      terminalOutput: [],
      fileOperations: [],
      buildStatus: 'idle',
      deployStatus: 'idle',
      lastFileChange: null,
      // Agentic AI state
      thinkingBlocks: [],
      toolOperations: [],
      pendingQuestion: null,
    }),
}));
